import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../services/supabaseClient";
import Reader from "./Reader";
import { BookOpenIcon, UploadIcon } from "./SvgIcons";
import { useLanguage } from "../contexts/LanguageContext";
import {
  normalizeOperationFailure,
  type OperationFailure,
} from "../services/operationFailure";

const PASTE_MIN_CHARS = 200;
const PASTE_MAX_CHARS = 10000;
const TXT_MAX_BYTES = 1024 * 1024;
const EPUB_MAX_BYTES = 10 * 1024 * 1024;
const USER_READING_ADAPT_PROMPT_VERSION = "user_reading_adapt_v11";
const USER_READING_C2_PASSTHROUGH_PROMPT_VERSION =
  "user_reading_c2_passthrough_v1";
const ACTIVE_READER_SECTION_STORAGE_PREFIX =
  "pagebunny:active-reader-section:";

type SourceType =
  | "pasted_text"
  | "txt_upload"
  | "epub_upload"
  | "extension_selection";


interface ReadingSection {
  id: string;
  reading_item_id: string;
  section_index: number;
  title: string | null;
  source_text: string;
  word_count: number | null;
  estimated_minutes: number | null;
  last_opened_at: string | null;
}

interface ReadingItem {
  id: string;
  user_id: string;
  source_type: SourceType;
  title: string | null;
  source_url: string | null;
  source_domain: string | null;
  source_file_path: string | null;
  status: "draft" | "ready" | "parsing" | "failed";
  parse_error: string | null;
  word_count: number | null;
  created_at: string;
  updated_at: string;
  last_opened_at: string | null;
  user_reading_sections?: ReadingSection[];
}

interface AdjustedReadingResponse {
  id?: string;
  adjusted_section_id?: string;
  adjusted_text?: string;
  adjusted_content?: string;
  saved_vocabulary_usage?: unknown;
  level_notes?: string | null;
  status?: string;
  target_level?: string;
  prompt_version?: string;
}

interface ImportReadingResponse {
  item_id?: string;
  reading_item_id?: string;
  status?: string;
}

interface AdjustedSectionSummary {
  id: string;
  section_id: string;
  adjusted_text: string;
  saved_vocabulary_usage: unknown;
  level_notes: string | null;
  target_level: string;
  prompt_version: string;
}

interface ActiveReaderSnapshot {
  sectionId: string;
  targetLevel?: string;
  promptVersion?: string;
  adjustedText?: string;
  adjustedSectionId?: string | null;
  adaptationLevelNotes?: string | null;
  injectedVocab?: string[];
}

type SectionFailureState =
  | { kind: "failed"; failure: OperationFailure }
  | { kind: "generating"; message: string };

interface LibraryProps {
  userId: string;
  targetTier: string;
  routeSectionId?: string | null;
  resetToLibrarySignal?: number;
  debugModeEnabled?: boolean;
  unavailableNotice?: boolean;
  onRequestSection: (sectionId: string) => void;
  onExitReader: () => void;
  onRouteSectionUnavailable: (sectionId: string) => void;
}

const formatDate = (value: string | null, language: string): string => {
  if (!value) return "";
  return new Date(value).toLocaleDateString(language === "zh" ? "zh-CN" : "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getSortedSections = (item: ReadingItem | null): ReadingSection[] =>
  [...(item?.user_reading_sections || [])].sort(
    (a, b) => a.section_index - b.section_index
  );

const getLastOpenedSection = (
  item: ReadingItem | null,
): ReadingSection | null => {
  const sections = getSortedSections(item);
  if (sections.length === 0) return null;

  return sections.reduce((latest, section) => {
    const latestTime = latest.last_opened_at
      ? Date.parse(latest.last_opened_at)
      : Number.NEGATIVE_INFINITY;
    const sectionTime = section.last_opened_at
      ? Date.parse(section.last_opened_at)
      : Number.NEGATIVE_INFINITY;
    const normalizedLatestTime = Number.isFinite(latestTime)
      ? latestTime
      : Number.NEGATIVE_INFINITY;
    const normalizedSectionTime = Number.isFinite(sectionTime)
      ? sectionTime
      : Number.NEGATIVE_INFINITY;

    if (normalizedSectionTime > normalizedLatestTime) return section;
    if (
      normalizedSectionTime === normalizedLatestTime &&
      normalizedSectionTime !== Number.NEGATIVE_INFINITY &&
      section.section_index > latest.section_index
    ) {
      return section;
    }
    return latest;
  }, sections[0]);
};

const normalizeVocabUsage = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (
          entry &&
          typeof entry === "object" &&
          "word" in entry &&
          typeof entry.word === "string"
        ) {
          return entry.word;
        }
        return "";
      })
      .map((word) => word.trim())
      .filter(Boolean);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((word) => word.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeTargetTier = (value: string): string =>
  value.trim().toUpperCase();

const getExpectedReadingPromptVersion = (targetLevel: string): string =>
  targetLevel === "C2"
    ? USER_READING_C2_PASSTHROUGH_PROMPT_VERSION
    : USER_READING_ADAPT_PROMPT_VERSION;

const isStoredReaderSnapshotEligible = (
  snapshot: ActiveReaderSnapshot,
  targetLevel: string,
): boolean => {
  if (!snapshot.adjustedText) return false;
  const storedTargetLevel = snapshot.targetLevel
    ? normalizeTargetTier(snapshot.targetLevel)
    : null;
  if (storedTargetLevel && storedTargetLevel !== targetLevel) {
    return false;
  }

  if (targetLevel === "C2") {
    return (
      snapshot.promptVersion === USER_READING_C2_PASSTHROUGH_PROMPT_VERSION ||
      (!snapshot.promptVersion && storedTargetLevel === "C2")
    );
  }

  return snapshot.promptVersion === USER_READING_ADAPT_PROMPT_VERSION;
};

const isReadyImportResponse = (
  response: ImportReadingResponse | null,
): boolean => response?.status === "ready";

const isStatusMissingImportResponse = (
  response: ImportReadingResponse | null,
): boolean =>
  !response?.status && Boolean(response?.item_id || response?.reading_item_id);

const getActiveReaderSectionStorageKey = (userId: string): string =>
  `${ACTIVE_READER_SECTION_STORAGE_PREFIX}${userId}`;

const readStoredActiveReaderSnapshot = (
  userId: string,
): ActiveReaderSnapshot | null => {
  try {
    const rawValue = window.localStorage.getItem(
      getActiveReaderSectionStorageKey(userId),
    );
    if (!rawValue) return null;

    if (!rawValue.trim().startsWith("{")) {
      return { sectionId: rawValue };
    }

    const parsed = JSON.parse(rawValue) as Partial<ActiveReaderSnapshot>;
    if (typeof parsed.sectionId !== "string" || !parsed.sectionId) {
      return null;
    }

    return {
      sectionId: parsed.sectionId,
      targetLevel:
        typeof parsed.targetLevel === "string" ? parsed.targetLevel : undefined,
      promptVersion:
        typeof parsed.promptVersion === "string"
          ? parsed.promptVersion
          : undefined,
      adjustedText:
        typeof parsed.adjustedText === "string" ? parsed.adjustedText : undefined,
      adjustedSectionId:
        typeof parsed.adjustedSectionId === "string"
          ? parsed.adjustedSectionId
          : null,
      adaptationLevelNotes:
        typeof parsed.adaptationLevelNotes === "string"
          ? parsed.adaptationLevelNotes
          : null,
      injectedVocab: Array.isArray(parsed.injectedVocab)
        ? parsed.injectedVocab.filter(
            (word): word is string => typeof word === "string",
          )
        : [],
    };
  } catch {
    return null;
  }
};

const storeActiveReaderSnapshot = (
  userId: string,
  snapshot: ActiveReaderSnapshot,
): void => {
  try {
    window.localStorage.setItem(
      getActiveReaderSectionStorageKey(userId),
      JSON.stringify(snapshot),
    );
  } catch {
    // Reader persistence is best-effort when storage is unavailable.
  }
};

const clearStoredActiveReaderSectionId = (userId: string): void => {
  try {
    window.localStorage.removeItem(getActiveReaderSectionStorageKey(userId));
  } catch {
    // Reader persistence is best-effort when storage is unavailable.
  }
};

const buildReaderSnapshot = (
  sectionId: string,
  readyAdjusted: AdjustedSectionSummary,
  targetLevel: string,
): ActiveReaderSnapshot => ({
  sectionId,
  targetLevel,
  promptVersion: getExpectedReadingPromptVersion(targetLevel),
  adjustedText: readyAdjusted.adjusted_text,
  adjustedSectionId: readyAdjusted.id,
  adaptationLevelNotes:
    targetLevel === "C2" ? null : readyAdjusted.level_notes,
  injectedVocab: normalizeVocabUsage(readyAdjusted.saved_vocabulary_usage),
});

const Library: React.FC<LibraryProps> = ({
  userId,
  targetTier,
  routeSectionId,
  resetToLibrarySignal,
  debugModeEnabled = false,
  unavailableNotice = false,
  onRequestSection,
  onExitReader,
  onRouteSectionUnavailable,
}) => {
  const { language, t } = useLanguage();
  const [items, setItems] = useState<ReadingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pasteFailure, setPasteFailure] = useState<OperationFailure | null>(null);
  const [submittingPaste, setSubmittingPaste] = useState(false);
  const [uploadFailure, setUploadFailure] =
    useState<OperationFailure | null>(null);
  const [submittingUpload, setSubmittingUpload] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [readerLoading, setReaderLoading] = useState<boolean>(false);
  const [adjustedText, setAdjustedText] = useState<string>("");
  const [adjustedSectionId, setAdjustedSectionId] = useState<string | null>(
    null
  );
  const [adaptationLevelNotes, setAdaptationLevelNotes] = useState<
    string | null
  >(null);
  const [injectedVocab, setInjectedVocab] = useState<string[]>([]);
  const [completedSectionIds, setCompletedSectionIds] = useState<Set<string>>(
    new Set()
  );
  const [readyAdjustedSections, setReadyAdjustedSections] = useState<
    Record<string, AdjustedSectionSummary>
  >({});
  const readySectionUpdateVersionRef = useRef<Record<string, number>>({});
  const readyUpdateVersionRef = useRef(0);
  const readySnapshotGenerationRef = useRef(0);
  const [generatingSectionIds, setGeneratingSectionIds] = useState<Set<string>>(
    new Set(),
  );
  const [sectionFailures, setSectionFailures] = useState<
    Record<string, SectionFailureState>
  >({});
  const sectionFailureRefs = useRef<
    Record<string, HTMLDivElement | null>
  >({});
  const pendingSectionFailureFocusRef = useRef<string | null>(null);
  const sectionListScrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingSectionListScrollItemIdRef = useRef<string | null>(null);

  const [title, setTitle] = useState<string>("");
  const [sourceUrl, setSourceUrl] = useState<string>("");
  const [sourceText, setSourceText] = useState<string>("");
  const [uploadTitle, setUploadTitle] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [retryingItemId, setRetryingItemId] = useState<string | null>(null);
  const [isDraggingUpload, setIsDraggingUpload] = useState<boolean>(false);
  const pasteTextRef = useRef<HTMLTextAreaElement | null>(null);
  const pasteErrorRef = useRef<HTMLDivElement | null>(null);
  const uploadErrorRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const requestedRouteSectionId = routeSectionId || null;
  const activeSectionIdRef = useRef<string | null>(requestedRouteSectionId);
  const previousRouteSectionIdRef = useRef<string | null>(
    requestedRouteSectionId,
  );
  const handledRouteSectionIdRef = useRef<string | null>(null);
  const libraryRoutePresentationClearedRef = useRef(false);
  const [storedReaderSnapshot, setStoredReaderSnapshot] =
    useState<ActiveReaderSnapshot | null>(() =>
      readStoredActiveReaderSnapshot(userId),
    );
  const lastResetToLibrarySignal = useRef(resetToLibrarySignal);

  if (previousRouteSectionIdRef.current !== requestedRouteSectionId) {
    previousRouteSectionIdRef.current = requestedRouteSectionId;
    activeSectionIdRef.current = requestedRouteSectionId;
  }

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) || null,
    [items, selectedItemId]
  );
  const normalizedTargetTier = useMemo(
    () => normalizeTargetTier(targetTier),
    [targetTier]
  );
  const sections = useMemo(() => getSortedSections(selectedItem), [selectedItem]);
  const activeSection = useMemo(
    () => sections.find((section) => section.id === activeSectionId) || null,
    [sections, activeSectionId]
  );
  const activeSectionIndex = activeSection
    ? sections.findIndex((section) => section.id === activeSection.id)
    : -1;

  useEffect(() => {
    setStoredReaderSnapshot(readStoredActiveReaderSnapshot(userId));
    handledRouteSectionIdRef.current = null;
  }, [userId]);

  useEffect(() => {
    if (
      resetToLibrarySignal === undefined ||
      lastResetToLibrarySignal.current === resetToLibrarySignal
    ) {
      return;
    }

    lastResetToLibrarySignal.current = resetToLibrarySignal;
    clearStoredActiveReaderSectionId(userId);
    setStoredReaderSnapshot(null);
    setSelectedItemId(null);
    activeSectionIdRef.current = null;
    setActiveSectionId(null);
    setReaderLoading(false);
    setGeneratingSectionIds(new Set());
    setSectionFailures({});
    setActionError(null);
    setAdjustedText("");
    setAdjustedSectionId(null);
    setAdaptationLevelNotes(null);
    setInjectedVocab([]);
    handledRouteSectionIdRef.current = null;
  }, [resetToLibrarySignal, userId]);

  const fetchItems = useCallback(async (): Promise<ReadingItem[]> => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from("user_reading_items")
      .select(
        `
        id,
        user_id,
        source_type,
        title,
        source_url,
        source_domain,
        source_file_path,
        status,
        parse_error,
        word_count,
        created_at,
        updated_at,
        last_opened_at,
        user_reading_sections!user_reading_sections_reading_item_id_fkey (
          id,
          reading_item_id,
          section_index,
          title,
          source_text,
          word_count,
          estimated_minutes,
          last_opened_at
        )
      `
      )
      .eq("user_id", userId)
      .order("updated_at", { ascending: false });

    if (fetchError) {
      console.error("Error loading Library:", fetchError);
      setError(t("library.loadError"));
      setLoading(false);
      return [];
    } else {
      setItems((data || []) as ReadingItem[]);
      setLoading(false);
      return (data || []) as ReadingItem[];
    }
  }, [t, userId]);

  const fetchCompletedSections = useCallback(async (): Promise<Set<string>> => {
    const { data, error: fetchError } = await supabase
      .from("progress_logs")
      .select("reading_section_id")
      .eq("user_id", userId)
      .eq("event_type", "article_read")
      .not("reading_section_id", "is", null);

    if (fetchError) {
      console.error("Error loading completed Library sections:", fetchError);
      return new Set();
    }

    const completed = new Set(
      (data || [])
        .map((log) => log.reading_section_id)
        .filter((sectionId): sectionId is string => Boolean(sectionId))
    );
    setCompletedSectionIds(completed);
    return completed;
  }, [userId]);

  const fetchReadyAdjustedSections = useCallback(async (sectionIds?: string[]) => {
    const refreshStartedAt = readyUpdateVersionRef.current;
    const snapshotGeneration = sectionIds?.length
      ? null
      : ++readySnapshotGenerationRef.current;
    let query = supabase
      .from("adjusted_reading_sections")
      .select(
        "id, section_id, adjusted_text, saved_vocabulary_usage, level_notes, target_level, prompt_version"
      )
      .eq("user_id", userId)
      .eq("target_level", normalizedTargetTier)
      .eq("status", "ready")
      .eq(
        "prompt_version",
        getExpectedReadingPromptVersion(normalizedTargetTier),
      );

    if (sectionIds?.length) {
      query = query.in("section_id", sectionIds);
    }

    const { data, error: fetchError } = await query
      .order("updated_at", { ascending: false });

    if (fetchError) {
      console.error("Error loading ready adjusted Library sections:", fetchError);
      return {};
    }

    const bySection: Record<string, AdjustedSectionSummary> = {};
    (data || []).forEach((section) => {
      if (!bySection[section.section_id]) {
        bySection[section.section_id] = section as AdjustedSectionSummary;
      }
    });
    if (sectionIds?.length) {
      Object.keys(bySection).forEach((sectionId) => {
        readyUpdateVersionRef.current += 1;
        readySectionUpdateVersionRef.current[sectionId] =
          readyUpdateVersionRef.current;
      });
    }
    if (
      snapshotGeneration !== null &&
      snapshotGeneration !== readySnapshotGenerationRef.current
    ) {
      return bySection;
    }
    setReadyAdjustedSections((current) => {
      const next = sectionIds?.length ? { ...current, ...bySection } : bySection;

      Object.entries(current).forEach(([sectionId, summary]) => {
        if (
          (readySectionUpdateVersionRef.current[sectionId] || 0) >
          refreshStartedAt
        ) {
          next[sectionId] = summary;
        }
      });

      return next;
    });
    return bySection;
  }, [normalizedTargetTier, userId]);

  useEffect(() => {
    fetchItems();
    fetchCompletedSections();
    fetchReadyAdjustedSections();
  }, [fetchItems, fetchCompletedSections, fetchReadyAdjustedSections]);

  const refreshLibrary = useCallback(async (): Promise<ReadingItem[]> => {
    const [refreshedItems] = await Promise.all([
      fetchItems(),
      fetchCompletedSections(),
      fetchReadyAdjustedSections(),
    ]);
    return refreshedItems;
  }, [fetchItems, fetchCompletedSections, fetchReadyAdjustedSections]);

  useEffect(() => {
    pasteErrorRef.current?.focus();
  }, [pasteFailure]);

  useEffect(() => {
    uploadErrorRef.current?.focus();
  }, [uploadFailure]);

  useLayoutEffect(() => {
    if (
      !selectedItem ||
      pendingSectionListScrollItemIdRef.current !== selectedItem.id
    ) {
      return;
    }

    const scrollRegion = sectionListScrollRef.current;
    const targetSection = getLastOpenedSection(selectedItem);
    const targetRow = targetSection
      ? sectionRowRefs.current[targetSection.id]
      : null;

    if (!scrollRegion || !targetRow) return;

    scrollRegion.style.paddingBottom = "0px";
    const targetTop =
      targetRow.getBoundingClientRect().top -
      scrollRegion.getBoundingClientRect().top +
      scrollRegion.scrollTop;
    const isOverflowing = scrollRegion.scrollHeight > scrollRegion.clientHeight;
    const trailingContentHeight =
      scrollRegion.scrollHeight - targetTop - targetRow.offsetHeight;
    const requiredTrailingSpace = isOverflowing
      ? Math.max(
          0,
          scrollRegion.clientHeight -
            targetRow.offsetHeight -
            trailingContentHeight,
        )
      : 0;
    scrollRegion.style.paddingBottom = `${requiredTrailingSpace}px`;
    scrollRegion.scrollTop = Math.max(0, targetTop);
    pendingSectionListScrollItemIdRef.current = null;
  }, [selectedItem]);

  useEffect(() => {
    const sectionId = pendingSectionFailureFocusRef.current;
    if (!sectionId || sectionFailures[sectionId]?.kind !== "failed") return;

    sectionFailureRefs.current[sectionId]?.focus();
    pendingSectionFailureFocusRef.current = null;
  }, [sectionFailures]);

  const displayAdjustedSection = useCallback(
    (
      item: ReadingItem,
      section: ReadingSection,
      snapshot: ActiveReaderSnapshot,
    ) => {
      if (activeSectionIdRef.current !== section.id) return;

      setSelectedItemId(item.id);
      setActiveSectionId(section.id);
      setReaderLoading(false);
      setGeneratingSectionIds((current) => {
        const next = new Set(current);
        next.delete(section.id);
        return next;
      });
      setSectionFailures((current) => {
        const { [section.id]: _resolved, ...remaining } = current;
        return remaining;
      });
      setActionError(null);
      setAdjustedText(snapshot.adjustedText || "");
      setAdjustedSectionId(snapshot.adjustedSectionId || null);
      setAdaptationLevelNotes(snapshot.adaptationLevelNotes ?? null);
      setInjectedVocab(snapshot.injectedVocab || []);
      storeActiveReaderSnapshot(userId, snapshot);
      setStoredReaderSnapshot(snapshot);
    },
    [userId],
  );

  const markSectionOpened = useCallback(async (
    item: ReadingItem,
    section: ReadingSection,
  ) => {
    const openedAt = new Date().toISOString();

    setItems((currentItems) =>
      currentItems.map((currentItem) =>
        currentItem.id === item.id
          ? {
              ...currentItem,
              last_opened_at: openedAt,
              user_reading_sections:
                currentItem.user_reading_sections?.map((currentSection) =>
                  currentSection.id === section.id
                    ? { ...currentSection, last_opened_at: openedAt }
                    : currentSection
                ),
            }
          : currentItem
      )
    );

    const [itemUpdate, sectionUpdate] = await Promise.all([
      supabase
        .from("user_reading_items")
        .update({ last_opened_at: openedAt })
        .eq("id", item.id)
        .eq("user_id", userId),
      supabase
        .from("user_reading_sections")
        .update({ last_opened_at: openedAt })
        .eq("id", section.id)
        .eq("reading_item_id", item.id)
        .eq("user_id", userId),
    ]);

    if (itemUpdate.error || sectionUpdate.error) {
      console.error(
        "Error saving last opened reading section:",
        itemUpdate.error || sectionUpdate.error,
      );
    }
  }, [userId]);

  const openSection = useCallback(async (item: ReadingItem, section: ReadingSection) => {
    if (activeSectionIdRef.current !== section.id) return;

    storeActiveReaderSnapshot(userId, { sectionId: section.id });
    setStoredReaderSnapshot({ sectionId: section.id });
    setSelectedItemId(item.id);
    setActiveSectionId(section.id);
    setReaderLoading(true);
    setActionError(null);
    setSectionFailures((current) => {
      const { [section.id]: _retrying, ...remaining } = current;
      return remaining;
    });
    setAdjustedText("");
    setAdjustedSectionId(null);
    setAdaptationLevelNotes(null);
    setInjectedVocab([]);

    await markSectionOpened(item, section);

    const readyAdjusted = readyAdjustedSections[section.id];
    if (readyAdjusted) {
      const snapshot = buildReaderSnapshot(
        section.id,
        readyAdjusted,
        normalizedTargetTier,
      );

      if (activeSectionIdRef.current === section.id) {
        displayAdjustedSection(item, section, snapshot);
      }
      return;
    }

    setGeneratingSectionIds((current) => new Set(current).add(section.id));

    const { data, error: invokeError } = await supabase.functions.invoke(
      "adjustUserReadingSection",
      {
        body: {
          section_id: section.id,
          target_level: normalizedTargetTier,
        },
      }
    );

    if (invokeError) {
      const failure = await normalizeOperationFailure(
        invokeError,
        t("library.adjustFailure"),
      );
      if (activeSectionIdRef.current === section.id) {
        pendingSectionFailureFocusRef.current = section.id;
        setSectionFailures((current) => ({
          ...current,
          [section.id]: { kind: "failed", failure },
        }));
        setReaderLoading(false);
      }
      setGeneratingSectionIds((current) => {
        const next = new Set(current);
        next.delete(section.id);
        return next;
      });
      return;
    }

    const response = data as AdjustedReadingResponse;
    const adjusted = response.adjusted_text || response.adjusted_content || "";
    if (!adjusted && response.status === "generating") {
      if (activeSectionIdRef.current === section.id) {
        setSectionFailures((current) => ({
          ...current,
          [section.id]: {
            kind: "generating",
            message: t("library.stillSimplifying"),
          },
        }));
        setReaderLoading(false);
      }
      setGeneratingSectionIds((current) => {
        const next = new Set(current);
        next.delete(section.id);
        return next;
      });
      return;
    }

    const vocab = normalizeVocabUsage(response.saved_vocabulary_usage);

    const nextAdaptationLevelNotes =
      normalizedTargetTier === "C2" ? null : response.level_notes ?? null;
    const nextPromptVersion =
      response.prompt_version ||
      getExpectedReadingPromptVersion(normalizedTargetTier);
    const nextAdjustedSectionId = response.adjusted_section_id || response.id || null;
    const isActiveSection = activeSectionIdRef.current === section.id;
    if (adjusted && isActiveSection) {
      setAdjustedText(adjusted);
      setAdaptationLevelNotes(nextAdaptationLevelNotes);
      setInjectedVocab(vocab);
      setAdjustedSectionId(nextAdjustedSectionId);
      storeActiveReaderSnapshot(userId, {
        sectionId: section.id,
        targetLevel: normalizedTargetTier,
        promptVersion: nextPromptVersion,
        adjustedText: adjusted,
        adjustedSectionId: nextAdjustedSectionId,
        adaptationLevelNotes: nextAdaptationLevelNotes,
        injectedVocab: vocab,
      });
      setStoredReaderSnapshot({
        sectionId: section.id,
        targetLevel: normalizedTargetTier,
        promptVersion: nextPromptVersion,
        adjustedText: adjusted,
        adjustedSectionId: nextAdjustedSectionId,
        adaptationLevelNotes: nextAdaptationLevelNotes,
        injectedVocab: vocab,
      });
    }
    if (
      adjusted &&
      response.status === "ready" &&
      nextPromptVersion ===
        getExpectedReadingPromptVersion(normalizedTargetTier)
    ) {
      readyUpdateVersionRef.current += 1;
      readySectionUpdateVersionRef.current[section.id] =
        readyUpdateVersionRef.current;
      setReadyAdjustedSections((current) => ({
        ...current,
        [section.id]: {
          id: response.adjusted_section_id || response.id || "",
          section_id: section.id,
          adjusted_text: adjusted,
          saved_vocabulary_usage: response.saved_vocabulary_usage ?? [],
          level_notes: response.level_notes ?? null,
          target_level: response.target_level || normalizedTargetTier,
          prompt_version: nextPromptVersion,
        },
      }));
    }
    if (adjusted && isActiveSection) {
      setSectionFailures((current) => {
        const { [section.id]: _resolved, ...remaining } = current;
        return remaining;
      });
    }
    if (isActiveSection) {
      setReaderLoading(false);
    }
    setGeneratingSectionIds((current) => {
      const next = new Set(current);
      next.delete(section.id);
      return next;
    });
  }, [
    displayAdjustedSection,
    markSectionOpened,
    normalizedTargetTier,
    readyAdjustedSections,
    t,
    userId,
  ]);

  const openItem = (item: ReadingItem) => {
    const isExpanding = selectedItemId !== item.id;
    pendingSectionListScrollItemIdRef.current = isExpanding ? item.id : null;
    setSelectedItemId((currentItemId) =>
      currentItemId === item.id ? null : item.id
    );
    if (selectedItemId !== item.id) {
      const sectionIds = getSortedSections(item).map((section) => section.id);
      void fetchReadyAdjustedSections(sectionIds);
    }
    setActiveSectionId(null);
    setAdjustedText("");
    setAdaptationLevelNotes(null);
    setActionError(null);
  };

  useEffect(() => {
    if (!requestedRouteSectionId) {
      if (libraryRoutePresentationClearedRef.current) return;
      libraryRoutePresentationClearedRef.current = true;
      handledRouteSectionIdRef.current = null;
      setSelectedItemId(null);
      setActiveSectionId(null);
      setReaderLoading(false);
      setAdjustedText("");
      setAdjustedSectionId(null);
      setAdaptationLevelNotes(null);
      setInjectedVocab([]);
      return;
    }

    libraryRoutePresentationClearedRef.current = false;
    if (
      loading ||
      error ||
      handledRouteSectionIdRef.current === requestedRouteSectionId
    ) {
      return;
    }

    for (const item of items) {
      const section = getSortedSections(item).find(
        (candidate) => candidate.id === requestedRouteSectionId
      );
      if (section) {
        activeSectionIdRef.current = requestedRouteSectionId;
        handledRouteSectionIdRef.current = requestedRouteSectionId;
        const storedSnapshotIsUsable =
          storedReaderSnapshot?.sectionId === requestedRouteSectionId &&
          isStoredReaderSnapshotEligible(
            storedReaderSnapshot,
            normalizedTargetTier,
          );

        if (storedSnapshotIsUsable) {
          void markSectionOpened(item, section);
          displayAdjustedSection(item, section, storedReaderSnapshot);
          return;
        }

        if (storedReaderSnapshot?.sectionId === requestedRouteSectionId) {
          clearStoredActiveReaderSectionId(userId);
          setStoredReaderSnapshot(null);
        }

        void openSection(item, section);
        return;
      }
    }

    handledRouteSectionIdRef.current = requestedRouteSectionId;
    clearStoredActiveReaderSectionId(userId);
    setStoredReaderSnapshot(null);
    setSelectedItemId(null);
    setActiveSectionId(null);
    setReaderLoading(false);
    setAdjustedText("");
    setAdjustedSectionId(null);
    setAdaptationLevelNotes(null);
    setInjectedVocab([]);
    onRouteSectionUnavailable(requestedRouteSectionId);
  }, [
    displayAdjustedSection,
    error,
    loading,
    items,
    markSectionOpened,
    normalizedTargetTier,
    onRouteSectionUnavailable,
    openSection,
    requestedRouteSectionId,
    storedReaderSnapshot,
    userId,
  ]);

  const runPasteImport = async () => {
    setPasteFailure(null);
    const trimmed = sourceText.trim();
    if (trimmed.length < PASTE_MIN_CHARS) {
      setPasteFailure({
        message: `Paste at least ${PASTE_MIN_CHARS} characters.`,
        retryable: false,
      });
      return;
    }
    if (trimmed.length > PASTE_MAX_CHARS) {
      setPasteFailure({
        message: `Paste text must stay under ${PASTE_MAX_CHARS} characters.`,
        retryable: false,
      });
      return;
    }

    setSubmittingPaste(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "importPastedReading",
        {
          body: {
            title: title.trim() || undefined,
            source_url: sourceUrl.trim() || undefined,
            source_text: trimmed,
          },
        }
      );

      if (invokeError) {
        setPasteFailure(
          await normalizeOperationFailure(invokeError, "Failed to import pasted text"),
        );
      } else if (isReadyImportResponse(data as ImportReadingResponse | null)) {
        setTitle("");
        setSourceUrl("");
        setSourceText("");
        const refreshedItems = await refreshLibrary();
        const importResponse = data as ImportReadingResponse;
        const itemId = importResponse.item_id;
        const fallbackId = importResponse.reading_item_id;
        const createdItem = refreshedItems.find(
          (item) => item.id === (itemId || fallbackId)
        );
        if (createdItem) {
          setSelectedItemId(createdItem.id);
          setActiveSectionId(null);
          setAdjustedText("");
          setAdaptationLevelNotes(null);
        }
      } else if (
        isStatusMissingImportResponse(data as ImportReadingResponse | null)
      ) {
        setPasteFailure({
          message: t("library.importStillProcessing"),
          code: "still_processing",
          retryable: true,
        });
      } else {
        setPasteFailure({
          message: "Failed to import pasted text",
          retryable: true,
        });
      }
    } catch (invokeError) {
      setPasteFailure(
        await normalizeOperationFailure(invokeError, "Failed to import pasted text"),
      );
    } finally {
      setSubmittingPaste(false);
    }
  };

  const submitPaste = (event: React.FormEvent) => {
    event.preventDefault();
    void runPasteImport();
  };

  const selectUploadFile = (file: File | null) => {
    setUploadFailure(null);
    setRetryingItemId(null);
    setSelectedFile(file);
  };

  const retryPersistedFileImport = (item: ReadingItem) => {
    setRetryingItemId(item.id);
    setUploadTitle(item.title ?? "");
    setUploadFailure(null);
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    selectUploadFile(event.target.files?.[0] || null);
  };

  const handleUploadDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.items.length > 0) {
      setIsDraggingUpload(true);
    }
  };

  const handleUploadDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setIsDraggingUpload(true);
  };

  const handleUploadDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDraggingUpload(false);
    }
  };

  const handleUploadDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingUpload(false);

    const file = event.dataTransfer.files?.[0] || null;
    if (!file) return;

    if (event.dataTransfer.files.length > 1) {
      setUploadFailure({
        message: "Upload one file at a time.",
        retryable: false,
      });
      return;
    }

    selectUploadFile(file);
  };

  const runFileImport = async (file = selectedFile) => {
    setUploadFailure(null);

    if (!file) {
      setUploadFailure({
        message: "Choose a .txt or .epub file first.",
        retryable: false,
      });
      return;
    }
    const normalizedFileName = file.name.toLowerCase();
    const isTxt = normalizedFileName.endsWith(".txt");
    const isEpub = normalizedFileName.endsWith(".epub");
    if (!isTxt && !isEpub) {
      setUploadFailure({
        message: "Only .txt and .epub files are supported.",
        retryable: false,
      });
      return;
    }
    if (isTxt && file.size > TXT_MAX_BYTES) {
      setUploadFailure({
        message: "TXT uploads must be 1 MB or smaller.",
        retryable: false,
      });
      return;
    }
    if (isEpub && file.size > EPUB_MAX_BYTES) {
      setUploadFailure({
        message: "EPUB uploads must be 10 MB or smaller.",
        retryable: false,
      });
      return;
    }

    setSubmittingUpload(true);
    const formData = new FormData();
    formData.append("file", file);
    if (uploadTitle.trim()) formData.append("title", uploadTitle.trim());

    const functionName = isEpub ? "importEpubReading" : "importTxtReading";
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        functionName,
        {
          body: formData,
        }
      );

      if (invokeError) {
        setUploadFailure(
          await normalizeOperationFailure(invokeError, "Failed to import file"),
        );
      } else if ((data as ImportReadingResponse | null)?.status === "parsing") {
        setUploadFailure({
          message: t("library.importStillProcessing"),
          code: "still_processing",
          retryable: true,
        });
      } else if (isReadyImportResponse(data as ImportReadingResponse | null)) {
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setUploadTitle("");
        const refreshedItems = await refreshLibrary();
        const importResponse = data as ImportReadingResponse;
        const itemId = importResponse.reading_item_id || importResponse.item_id;
        const createdItem = refreshedItems.find((item) => item.id === itemId);
        if (createdItem) {
          setSelectedItemId(createdItem.id);
          setActiveSectionId(null);
          setAdjustedText("");
          setAdaptationLevelNotes(null);
        }
      } else if (
        isStatusMissingImportResponse(data as ImportReadingResponse | null)
      ) {
        setUploadFailure({
          message: t("library.importStillProcessing"),
          code: "still_processing",
          retryable: true,
        });
      } else {
        setUploadFailure({ message: "Failed to import file", retryable: true });
      }
    } catch (invokeError) {
      setUploadFailure(
        await normalizeOperationFailure(invokeError, "Failed to import file"),
      );
    } finally {
      setSubmittingUpload(false);
    }
  };

  const submitUpload = (event: React.FormEvent) => {
    event.preventDefault();
    void runFileImport();
  };

  const deleteItem = async (item: ReadingItem) => {
    if (
      !window.confirm(
        t("library.deleteConfirm", {
          title: item.title || t("common.untitledReading"),
        })
      )
    ) {
      return;
    }

    setActionError(null);
    const { error: deleteError } = await supabase
      .from("user_reading_items")
      .delete()
      .eq("id", item.id)
      .eq("user_id", userId);

    if (deleteError) {
      setActionError("Failed to delete reading item.");
      return;
    }

    if (item.source_file_path) {
      await supabase.storage
        .from("user-reading-uploads")
        .remove([item.source_file_path]);
    }

    if (selectedItemId === item.id) {
      setSelectedItemId(null);
      activeSectionIdRef.current = null;
      setActiveSectionId(null);
      setAdjustedText("");
      setAdaptationLevelNotes(null);
    }
    await refreshLibrary();
  };

  const openAdjacentSection = (offset: number) => {
    const next = sections[activeSectionIndex + offset];
    if (next) onRequestSection(next.id);
  };

  const requestSection = (section: ReadingSection) =>
    onRequestSection(section.id);

  const retrySection = (item: ReadingItem, section: ReadingSection) => {
    if (section.id === requestedRouteSectionId) {
      void openSection(item, section);
      return;
    }
    onRequestSection(section.id);
  };

  const finishReading = async () => {
    clearStoredActiveReaderSectionId(userId);
    setStoredReaderSnapshot(null);
    setReaderLoading(false);
    setGeneratingSectionIds(new Set());
    setAdjustedText("");
    activeSectionIdRef.current = null;
    setActiveSectionId(null);
    setAdjustedSectionId(null);
    setAdaptationLevelNotes(null);
    setInjectedVocab([]);
    await Promise.all([fetchCompletedSections(), fetchReadyAdjustedSections()]);
    onExitReader();
  };

  if (
    adjustedText &&
    adjustedSectionId &&
    selectedItem &&
    activeSection &&
    activeSection.id === requestedRouteSectionId
  ) {
    return (
      <Reader
        heading={t("library.readerHeading", {
          title:
            activeSection.title ||
            selectedItem.title ||
            t("common.untitledReading"),
        })}
        simplifiedContent={adjustedText}
        injectedVocab={injectedVocab}
        userId={userId}
        debugModeEnabled={debugModeEnabled}
        targetLevel={normalizedTargetTier}
        onFinishReading={finishReading}
        originalContentOverride={activeSection.source_text}
        readingSectionId={activeSection.id}
        adjustedReadingSectionId={adjustedSectionId}
        adaptationLevelNotes={adaptationLevelNotes}
        sourceUrl={selectedItem.source_url}
        sourceDomain={selectedItem.source_domain}
        backLabel={t("library.backToLibrary")}
        hasPreviousSection={activeSectionIndex > 0}
        hasNextSection={activeSectionIndex >= 0 && activeSectionIndex < sections.length - 1}
        onPreviousSection={() => openAdjacentSection(-1)}
        onNextSection={() => openAdjacentSection(1)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {unavailableNotice && (
          <div role="status" tabIndex={-1} data-route-focus-priority>
            {t("library.readingUnavailable")}
          </div>
        )}
        <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1
              className="text-3xl font-bold text-gray-900"
              tabIndex={-1}
              {...(!requestedRouteSectionId
                ? { "data-route-focus": true }
                : {})}
            >
              {t("library.title")}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {t("library.subtitle")}
            </p>
          </div>
          <button
            onClick={() => refreshLibrary()}
            className="self-start rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("common.refresh")}
          </button>
        </div>

        {(error || actionError) && (
          <div
            role="alert"
            tabIndex={-1}
            data-route-focus-priority={
              requestedRouteSectionId ? true : undefined
            }
            className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error || actionError}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="min-w-0 space-y-4">
            {loading ? (
              <div className="rounded-lg bg-white p-8 text-center text-gray-600 shadow">
                {t("library.loading")}
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-lg border border-indigo-100 bg-white p-8 shadow-sm">
                <div className="mx-auto max-w-2xl text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                    <BookOpenIcon className="text-xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t("library.emptyTitle")}
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    {t("library.emptyBody")}
                  </p>
                  <p className="mt-3 text-sm font-medium text-gray-700">
                    {t("library.privateNote")}
                  </p>
                  <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                      {t("library.uploadCta")}
                    </button>
                    <button
                      type="button"
                      onClick={() => pasteTextRef.current?.focus()}
                      className="rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("library.pasteCta")}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              items.map((item) => {
                const itemSections = getSortedSections(item);
                const isSelected = selectedItemId === item.id;
                const adjustedSectionCount = itemSections.filter((section) =>
                  Boolean(readyAdjustedSections[section.id])
                ).length;
                const isCompleted =
                  itemSections.length > 0 &&
                  itemSections.every((section) =>
                    completedSectionIds.has(section.id)
                  );
                return (
                  <article
                    key={item.id}
                    className="min-w-0 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
                  >
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
	                      <button
	                        onClick={() => openItem(item)}
                        aria-expanded={isSelected}
	                        className="w-full min-w-0 flex-1 text-left"
	                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <h2
                            id={`reading-item-${item.id}-title`}
                            className="truncate text-lg font-semibold text-gray-900"
                          >
                            {item.title || t("common.untitledReading")}
                          </h2>
                          <span className="rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                            {item.source_type === "pasted_text"
                              ? t("library.sourcePasted")
                              : item.source_type === "txt_upload"
                              ? t("library.sourceTxt")
                              : item.source_type === "epub_upload"
                              ? t("library.sourceEpub")
                              : t("library.sourceExtension")}
                          </span>
                          <span
                            className={`rounded px-2 py-1 text-xs font-medium ${
                              item.status === "ready"
                                ? "bg-green-50 text-green-700"
                                : item.status === "failed"
                                ? "bg-red-50 text-red-700"
                                : "bg-yellow-50 text-yellow-700"
                            }`}
                          >
                            {item.status === "ready"
                              ? t("library.importReady")
                              : item.status}
                          </span>
                          {itemSections.length > 0 && (
                            <span
                              className={`rounded px-2 py-1 text-xs font-medium ${
                                adjustedSectionCount === itemSections.length
                                  ? "bg-indigo-50 text-indigo-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {t("library.simplifiedProgress", {
                                count: adjustedSectionCount,
                                total: itemSections.length,
                              })}
                            </span>
                          )}
                          {isCompleted && (
                            <span className="rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                              {t("common.completed")}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          <span>
                            {t("common.sectionCount", {
                              count: itemSections.length || 0,
                            })}
                          </span>
                          <span>
                            {t("common.words", { count: item.word_count || 0 })}
                          </span>
                          {item.source_domain && <span>{item.source_domain}</span>}
                          <span>
                            {item.last_opened_at
                              ? t("library.lastOpened", {
                                  date: formatDate(item.last_opened_at, language),
                                })
                              : t("library.notOpened")}
                          </span>
                        </div>
                        {item.parse_error && (
                          <p className="mt-2 text-sm text-red-600">
                            {item.parse_error}
                          </p>
                        )}
                      </button>
                      <div className="flex shrink-0 items-center gap-2">
                        {item.status === "failed" &&
                          (item.source_type === "txt_upload" ||
                            item.source_type === "epub_upload") && (
                            <button
                              type="button"
                              onClick={() => retryPersistedFileImport(item)}
                              aria-pressed={retryingItemId === item.id}
                              className="rounded-md border border-indigo-200 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                            >
                              {t("library.selectSameFile")}
                            </button>
                          )}
                        <button
                          onClick={() => openItem(item)}
	                          disabled={item.status !== "ready"}
	                          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
	                        >
                          {isSelected ? t("library.hideSections") : t("common.sections")}
	                        </button>
                        <button
                          onClick={() => deleteItem(item)}
                          className="rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </div>

                    {isSelected && itemSections.length > 0 && (
                      <div className="mt-4 border-t border-gray-100">
                        <div
                          ref={sectionListScrollRef}
                          role="region"
                          aria-labelledby={`reading-item-${item.id}-title`}
                          tabIndex={0}
                          className="max-h-96 divide-y divide-gray-100 overflow-y-auto overscroll-contain focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                        >
	                          {itemSections.map((section) => {
	                            const isGenerating =
	                              generatingSectionIds.has(section.id);
	                            const sectionFailure = sectionFailures[section.id];
	                            const isStillGenerating =
	                              sectionFailure?.kind === "generating";
                              const hasPermanentFailure =
                                sectionFailure?.kind === "failed" &&
                                !sectionFailure.failure.retryable;
	                            const sectionLabel =
	                              section.title || `Section ${section.section_index + 1}`;
                            const isSectionCompleted =
                              completedSectionIds.has(section.id);
                            const hasAdjusted =
                              Boolean(readyAdjustedSections[section.id]);
	                            return (
                              <div
                                key={section.id}
                                ref={(element) => {
                                  sectionRowRefs.current[section.id] = element;
                                }}
                                role="group"
                                aria-label={sectionLabel}
                                className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1">
                                  <div className="min-w-0 flex-1 truncate text-base font-medium text-gray-700">
                                    {sectionLabel}
                                  </div>
                                  <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-gray-500">
                                    <span className="whitespace-nowrap">
                                      {t("common.min", {
                                        count: section.estimated_minutes || 5,
                                      })}
                                    </span>
                                    {isGenerating ? (
                                      <span className="inline-flex items-center gap-2 rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                                        {t("library.simplifying")}
                                      </span>
	                                    ) : sectionFailure?.kind === "failed" ? (
	                                      <div
                                          ref={(element) => {
                                            sectionFailureRefs.current[section.id] =
                                              element;
                                          }}
	                                        role="alert"
                                          tabIndex={-1}
	                                        data-route-focus-priority={
	                                          section.id === requestedRouteSectionId
	                                            ? true
	                                            : undefined
	                                        }
	                                        className="inline-flex items-center gap-2 rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
	                                      >
	                                        <span>{sectionFailure.failure.message}</span>
                                          {sectionFailure.failure.retryable && (
	                                          <button
	                                            type="button"
	                                            onClick={() => retrySection(item, section)}
	                                            aria-label={`Retry simplification for ${sectionLabel}`}
	                                            className="font-medium underline"
	                                          >
	                                            {t("library.retrySimplification")}
	                                          </button>
                                          )}
	                                      </div>
	                                    ) : isStillGenerating ? (
	                                      <div
	                                        role="status"
	                                        tabIndex={-1}
	                                        data-route-focus-priority={
	                                          section.id === requestedRouteSectionId
	                                            ? true
	                                            : undefined
	                                        }
	                                        className="inline-flex items-center gap-2 rounded bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700"
	                                      >
	                                        <span>{sectionFailure.message}</span>
	                                        <button
	                                          type="button"
	                                          onClick={() => retrySection(item, section)}
	                                          aria-label={`Check again for simplification: ${sectionLabel}`}
	                                          className="font-medium underline"
	                                        >
	                                          {t("library.checkSimplificationAgain")}
	                                        </button>
	                                      </div>
                                    ) : isSectionCompleted ? (
                                      <span className="rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                                        {t("common.completed")}
                                      </span>
                                    ) : null}
	                                  </div>
	                                </div>
                                <button
                                  type="button"
                                  onClick={() => requestSection(section)}
	                                  disabled={isGenerating || hasPermanentFailure}
                                  aria-disabled={
                                    isGenerating || hasPermanentFailure
                                  }
                                  className="shrink-0 whitespace-nowrap text-sm font-bold text-indigo-600 hover:text-indigo-800 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline sm:w-36 sm:text-right"
                                >
	                                  {isGenerating
	                                    ? t("library.simplifying")
                                    : hasAdjusted
                                    ? t("library.read")
                                    : t("library.simplifyRead")}
	                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </section>

          <aside className="space-y-6">
            <form
              onSubmit={submitUpload}
              id="file-upload"
              className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                {t("library.uploadTitle")}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {t("library.uploadBody")}
              </p>
              {uploadFailure && (
                <div
                  ref={uploadErrorRef}
                  role="alert"
                  tabIndex={-1}
                  className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  <p>{uploadFailure.message}</p>
                  {uploadFailure.retryable && (
                    <button
                      type="button"
                      onClick={() => runFileImport()}
                      className="mt-2 font-medium underline"
                    >
                      {uploadFailure.code === "still_processing"
                        ? t("library.checkImportAgain")
                        : t("library.retryUpload")}
                    </button>
                  )}
                </div>
              )}
              <div className="mt-4 space-y-4">
                <input
                  value={uploadTitle}
                  onChange={(event) => setUploadTitle(event.target.value)}
                  placeholder={t("library.titleOptional")}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.epub,text/plain,application/epub+zip"
                  onChange={handleFileInputChange}
                  className="sr-only"
                />
                <div
                  onDragEnter={handleUploadDragEnter}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleUploadDrop}
                  className={`rounded-md border-2 border-dashed px-4 py-6 text-center transition ${
                    isDraggingUpload
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 bg-gray-50 hover:border-indigo-300"
                  }`}
                >
                  <UploadIcon className="mx-auto text-2xl text-indigo-600" />
                  <p className="mt-2 text-sm font-medium text-gray-800">
                    {selectedFile
                      ? selectedFile.name
                      : t("library.dropFile")}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {t("library.chooseFileLine")}
                  </p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {t("library.chooseFile")}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  {t("library.uploadLimits")}
                </p>
                <button
                  type="submit"
                  disabled={submittingUpload}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {submittingUpload ? t("library.uploading") : t("library.uploadFile")}
                </button>
              </div>
            </form>

            <form
              onSubmit={submitPaste}
              id="paste-reading"
              className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                {t("library.pasteTitle")}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {t("library.pasteBody")}
              </p>
              {pasteFailure && (
                <div
                  ref={pasteErrorRef}
                  role="alert"
                  tabIndex={-1}
                  className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  <p>{pasteFailure.message}</p>
                  {pasteFailure.retryable && (
                    <button
                      type="button"
                      onClick={runPasteImport}
                      className="mt-2 font-medium underline"
                    >
                      {pasteFailure.code === "still_processing"
                        ? t("library.checkImportAgain")
                        : t("library.retryPaste")}
                    </button>
                  )}
                </div>
              )}
              <div className="mt-4 space-y-4">
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={t("library.titlePlaceholder")}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <input
                  value={sourceUrl}
                  onChange={(event) => setSourceUrl(event.target.value)}
                  placeholder={t("library.sourceUrl")}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <div>
                  <textarea
                    ref={pasteTextRef}
                    value={sourceText}
                    onChange={(event) => setSourceText(event.target.value)}
                    placeholder={t("library.pastePlaceholder")}
                    rows={9}
                    className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <div className="mt-1 text-right text-xs text-gray-500">
                    {sourceText.length}/{PASTE_MAX_CHARS}
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submittingPaste}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {submittingPaste ? t("library.importing") : t("library.importPasted")}
                </button>
              </div>
            </form>
          </aside>
        </div>

        {readerLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="rounded-lg bg-white p-6 text-center shadow-xl">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
              <div className="text-sm font-medium text-gray-700">
                {t("library.creatingAdjusted")}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
